import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "maplibre-gl/dist/maplibre-gl.css";
import "@maplibre/maplibre-gl-leaflet";
import { severityColor, severityWeight, voivodeshipName } from "./constants";
import { Autocomplete } from "./autocomplete";
import {
  activeCategories,
  boundariesByTeryt,
  countyCodesForScope,
  countyNameByTeryt,
  currentAlerts,
  voivodeshipBoundariesByTeryt,
} from "./state";

export interface MapViewCallbacks {
  onCountySelected: (terytCode: string) => void;
}

let map: L.Map;
let baseCountyLayer: L.LayerGroup;
let voivodeshipBorderLayer: L.LayerGroup;
let alertLayer: L.LayerGroup;
let selectedCountyLayer: L.LayerGroup;
let callbacks: MapViewCallbacks;

export function initMapView(mapElementId: string, mapCallbacks: MapViewCallbacks): void {
  callbacks = mapCallbacks;

  map = L.map(mapElementId, { zoomControl: false }).setView([52.0, 19.3], 6);
  L.control.zoom({ position: "bottomleft" }).addTo(map);
  L.maplibreGL({ style: "https://tiles.openfreemap.org/styles/dark" }).addTo(map);

  baseCountyLayer = L.layerGroup().addTo(map);
  alertLayer = L.layerGroup().addTo(map);

  const voivodeshipPane = map.createPane("voivodeshipBorders");
  voivodeshipPane.style.zIndex = "450";
  voivodeshipPane.style.pointerEvents = "none";
  voivodeshipBorderLayer = L.layerGroup().addTo(map);

  const selectedCountyPane = map.createPane("selectedCounty");
  selectedCountyPane.style.zIndex = "460";
  selectedCountyPane.style.pointerEvents = "none";
  selectedCountyLayer = L.layerGroup().addTo(map);
}

export function highlightCounty(terytCode: string): void {
  selectedCountyLayer.clearLayers();
  const feature = boundariesByTeryt.get(terytCode);
  if (!feature) return;
  L.geoJSON(feature as GeoJSON.Feature, {
    pane: "selectedCounty",
    style: { color: "#ffffff", weight: 3, fill: false, interactive: false },
  }).addTo(selectedCountyLayer);
}

export function clearCountyHighlight(): void {
  selectedCountyLayer.clearLayers();
}

export function renderVoivodeshipBorders(): void {
  voivodeshipBorderLayer.clearLayers();
  for (const feature of voivodeshipBoundariesByTeryt.values()) {
    L.geoJSON(feature as GeoJSON.Feature, {
      pane: "voivodeshipBorders",
      style: { color: "#8a93a0", weight: 2, fill: false, interactive: false },
    }).addTo(voivodeshipBorderLayer);
  }
}

function bindCountyInteractions(layer: L.GeoJSON, terytCode: string): void {
  const name = countyNameByTeryt.get(terytCode) ?? terytCode;
  layer.bindTooltip(`Powiat ${name}`, { sticky: true, direction: "top", opacity: 0.9, className: "county-tooltip" });
  layer.on("click", () => {
    highlightCounty(terytCode);
    callbacks.onCountySelected(terytCode);
  });
}

export function renderBaseCounties(): void {
  baseCountyLayer.clearLayers();
  for (const [terytCode, feature] of boundariesByTeryt) {
    const layer = L.geoJSON(feature as GeoJSON.Feature, {
      style: { color: "#2a2d33", weight: 1, fillColor: "#000000", fillOpacity: 0.02 },
    });
    bindCountyInteractions(layer, terytCode);
    layer.on("mouseover", () => layer.setStyle({ weight: 2, color: "#565b64" }));
    layer.on("mouseout", () => layer.setStyle({ weight: 1, color: "#2a2d33" }));
    layer.addTo(baseCountyLayer);
  }
}

export function renderAlertLayer(): void {
  alertLayer.clearLayers();
  const filtered = currentAlerts.filter((alert) => activeCategories.has(alert.category));

  const alertsByCounty = new Map<string, typeof filtered>();
  for (const alert of filtered) {
    for (const terytCode of countyCodesForScope(alert)) {
      if (!alertsByCounty.has(terytCode)) {
        alertsByCounty.set(terytCode, []);
      }
      alertsByCounty.get(terytCode)!.push(alert);
    }
  }

  for (const [terytCode, alerts] of alertsByCounty) {
    const feature = boundariesByTeryt.get(terytCode);
    if (!feature) continue;
    const maxSeverity = Math.max(...alerts.map((alert) => alert.severity));
    const baseWeight = severityWeight(maxSeverity);
    const layer = L.geoJSON(feature as GeoJSON.Feature, {
      style: { color: "#0b0d10", weight: baseWeight, fillColor: severityColor(maxSeverity), fillOpacity: 0.65 },
    });
    bindCountyInteractions(layer, terytCode);
    layer.on("mouseover", () => layer.setStyle({ weight: baseWeight + 1, fillOpacity: 0.85 }));
    layer.on("mouseout", () => layer.setStyle({ weight: baseWeight, fillOpacity: 0.65 }));
    layer.addTo(alertLayer);
  }
}

export function zoomToVoivodeship(prefix: string): void {
  let bounds: L.LatLngBounds | null = null;
  for (const [terytCode, feature] of boundariesByTeryt) {
    if (!terytCode.startsWith(prefix)) continue;
    const featureBounds = L.geoJSON(feature as GeoJSON.Feature).getBounds();
    bounds = bounds ? bounds.extend(featureBounds) : featureBounds;
  }
  if (bounds) {
    map.fitBounds(bounds, { maxZoom: 9, padding: [40, 40] });
  }
}

function goToCounty(terytCode: string): void {
  const feature = boundariesByTeryt.get(terytCode);
  if (feature) {
    const bounds = L.geoJSON(feature as GeoJSON.Feature).getBounds();
    map.fitBounds(bounds, { maxZoom: 10, padding: [40, 40] });
  }
  highlightCounty(terytCode);
  callbacks.onCountySelected(terytCode);
}

interface CountyOption {
  value: string;
  label: string;
  hint?: string;
}

export function initMapSearch(inputId: string, resultsId: string): void {
  const input = document.getElementById(inputId) as HTMLInputElement;
  const resultsContainer = document.getElementById(resultsId) as HTMLElement;

  new Autocomplete<CountyOption>({
    input,
    resultsContainer,
    showOnEmptyFocus: false,
    getItems: (query) => {
      const q = query.trim().toLocaleLowerCase("pl-PL");
      if (!q) return [];
      return [...countyNameByTeryt.entries()]
        .filter(([, name]) => name.toLocaleLowerCase("pl-PL").includes(q))
        .sort((a, b) => a[1].localeCompare(b[1], "pl"))
        .slice(0, 20)
        .map(([terytCode, name]) => ({ value: terytCode, label: name, hint: voivodeshipName(terytCode.slice(0, 2)) }));
    },
    onSelect: (item) => {
      input.value = item.label;
      goToCounty(item.value);
    },
  });
}
